import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../useLanguage';
import logo from '../resources/images/Flex_CTO_cropped.png';

interface HeaderProps {
  activeSection: string;
}

const Header = ({ activeSection }: HeaderProps) => {
  const { isDark, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { id: 'home', label: t.header.home },
    { id: 'services', label: t.header.services },
    { id: 'about', label: t.header.about },
    { id: 'workshops', label: t.header.workshops },
    { id: 'contact', label: t.header.contact },
  ];

  const getDestination = (itemId: string) => {
    if (itemId === 'workshops') return '/workshops';
    if (location.pathname === '/workshops' && itemId === 'contact') return '#contact';
    return `/#${itemId}`;
  };

  return (
    <header className="fixed top-0 w-full bg-white/95 dark:bg-dark-900/95 backdrop-blur-lg z-50 border-b border-gray-200/50 dark:border-dark-700/50 shadow-sm transition-all duration-300">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary-700 focus:text-white focus:shadow-lg">
        {t.header.skipToContent}
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/#home" className="flex items-center space-x-3 rounded-lg" aria-label={t.header.homeLabel}>
            <img src={logo} alt="" width="94" height="40" className="h-10 w-auto transition-transform hover:scale-105" />
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Flex CTO</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t.header.tagline}</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2 xl:gap-4" aria-label={t.header.primaryNavigation}>
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={getDestination(item.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? 'text-primary-700 bg-primary-50 dark:text-primary-300 dark:bg-primary-900/20 shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-50 dark:hover:bg-dark-800'
                }`}
                aria-current={activeSection === item.id ? (item.id === 'workshops' ? 'page' : 'location') : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <div className="language-switch" role="group" aria-label={t.header.language}>
              {(['en', 'es'] as const).map((language) => (
                <button
                  key={language}
                  type="button"
                  className="language-option"
                  data-active={lang === language}
                  aria-pressed={lang === language}
                  aria-label={language === 'en' ? 'English' : 'Español'}
                  onClick={() => setLang(language)}
                >
                  {language.toUpperCase()}
                </button>
              ))}
            </div>

            <button
              onClick={toggleTheme}
              className="min-w-11 min-h-11 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-all duration-200 hover:scale-105"
              title={isDark ? t.header.lightMode : t.header.darkMode}
              aria-label={isDark ? t.header.lightMode : t.header.darkMode}
            >
              {isDark ? <span className="text-xl">☀</span> : <span className="text-xl">☾</span>}
            </button>
            
            <a
              href="https://calendly.com/benjamin-juarez/60min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary hidden xl:inline-flex items-center text-sm hover:scale-105 transition-transform"
            >
              {t.header.scheduleCall}
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden min-w-11 min-h-11 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
              aria-label={isMenuOpen ? t.header.closeMenu : t.header.openMenu}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav id="mobile-navigation" className="mobile-menu-shell lg:hidden" data-open={isMenuOpen} aria-hidden={!isMenuOpen} aria-label={t.header.mobileNavigation}>
          <div className="mobile-menu-content">
            <div className="py-4 border-t border-gray-200 dark:border-dark-700">
              <div className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.id}
                    to={getDestination(item.id)}
                    onClick={() => setIsMenuOpen(false)}
                    className={`min-h-11 px-3 py-2 rounded-md text-left transition-colors ${
                      activeSection === item.id
                        ? 'text-primary-700 bg-primary-50 dark:text-primary-300 dark:bg-primary-900/20'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                    tabIndex={isMenuOpen ? 0 : -1}
                    aria-current={activeSection === item.id ? (item.id === 'workshops' ? 'page' : 'location') : undefined}
                  >
                    {item.label}
                  </Link>
                ))}
                <a
                  href="https://calendly.com/benjamin-juarez/60min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-4 text-center"
                  tabIndex={isMenuOpen ? 0 : -1}
                >
                  {t.header.scheduleCall}
                </a>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
